import React, { useEffect, useState, useRef } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import { Button, Dialog, DialogHeader, DialogBody, DialogFooter, Chip } from "@material-tailwind/react";
import ClipLoader from "react-spinners/ClipLoader";


const DEFAULT_BUTTONS = [{ title: "View", variant: "secondary" }];
const showFutureEvents = true;
const API_URL = `/api/events/GetEvents?showFutureEvents=${showFutureEvents}`;

const currentYear = new Date().getFullYear();
const season = `${currentYear}-${currentYear + 1}`;
export const TimelineDefaults = {
    tagline: season,
    heading: "Events",
    description: `Check out our events for the ${season} season!!`,
    timelines: [],
};

export const TimelineEvents = (props) => {
    const { tagline, heading, description, timelines } = {
        ...TimelineDefaults,
        ...props,
    };

    const [open, setOpen] = useState(false);
    const [selectedTimeline, setSelectedTimeline] = useState(null);
    const [currentTimelines, setCurrentTimelines] = useState([]);
    const [loading, setLoading] = useState(true);  // NEW: loading state

    const handleOpen = (timeline) => {
        setSelectedTimeline(timeline);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedTimeline(null);
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true); // NEW: start loading
            try {
                const response = await fetch(API_URL);
                const data = await response.json();

                const timelinesWithDefaults = data.map(event => {
                    const startDate = new Date(event.startDate);

                    const dateString = startDate.toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    });

                    const startTimeString = startDate.toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                    }).toLowerCase();

                    let endTimeString = "";
                    if (event.endTime) {
                        const [h, m, s] = event.endTime.split(":").map(Number);
                        const endDate = new Date(startDate);
                        endDate.setHours(h, m, s || 0);

                        endTimeString = endDate.toLocaleTimeString([], {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                        }).toLowerCase();
                    }

                    return {
                        ...event,
                        buttons: DEFAULT_BUTTONS,
                        date: endTimeString
                            ? `${dateString} ${startTimeString} - ${endTimeString}`
                            : `${dateString} ${startTimeString}`,
                    };
                });
                setCurrentTimelines(timelinesWithDefaults);
            } catch (error) {
                console.error("Error fetching timeline data:", error);
            } finally {
                setLoading(false);  // NEW: stop loading
            }
        };

        fetchData();
    }, []);

    // NEW: Show spinner while loading
    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <ClipLoader size={60} color={"#FF7F50"} />
            </div>
        );
    }

    // rest of your return code unchanged
    return (
        <section className="px-[5%] py-16 md:py-24 lg:py-28">
            <div className="container">
                <div className="relative grid auto-cols-fr grid-cols-1 items-start justify-center gap-6 sm:gap-12 md:grid-cols-2 md:gap-24 lg:gap-32">
                    <div className="relative mt-30 z-10 md:sticky md:top-1/2 md:-translate-y-1/2 md:z-auto md:pr-4 text-center md:text-left">
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            viewport={{ once: true }}
                        >
                            <p className="text-sm font-semibold text-red-600 uppercase tracking-widest mb-3">
                                {tagline}
                            </p>
                            <h1 className="text-5xl font-extrabold text-gray-900 sm:text-6xl lg:text-7xl leading-tight mb-4">
                                {heading}
                            </h1>
                            <div className="h-1 w-16 bg-red-500 rounded-full mb-6 mx-auto md:mx-0"></div>
                            <p className="text-lg text-gray-600 max-w-xl">
                                {description}
                            </p>
                        </motion.div>
                    </div>
                    <div className="absolute z-0 flex h-full w-8 flex-col items-center justify-self-start [grid-area:2/1/3/2] md:z-auto md:justify-self-center md:[grid-area:auto]">
                        <div className="absolute z-10 h-16 w-1 bg-gradient-to-b from-background-primary to-transparent" />
                        <div className="sticky top-0 mt-[-50vh] h-[50vh] w-[3px] bg-black" />
                        <div className="h-full w-[3px] bg-neutral-lighter" />
                        <div className="absolute bottom-0 z-0 h-16 w-1 bg-gradient-to-b from-transparent to-background-primary" />
                        <div className="absolute top-[-50vh] h-[50vh] w-full bg-background-primary" />
                    </div>

                    <motion.div
                        className="grid auto-cols-fr gap-x-12 gap-y-8 sm:gap-y-12 md:gap-x-20 md:gap-y-20"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        variants={{
                            hidden: { opacity: 0, y: 50 },
                            visible: {
                                opacity: 1,
                                y: 0,
                                transition: { staggerChildren: 0.15, duration: 0.6, ease: "easeOut" }
                            }
                        }}
                    >
                        {currentTimelines.map((timeline, index) => (
                            <motion.div
                                key={index}
                                variants={{
                                    hidden: { opacity: 0, y: 30 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                            >
                                <Timeline timeline={timeline} onButtonClick={handleOpen} />
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* Dialog for displaying timeline details */}
            {selectedTimeline && (
                <Dialog
                    open={open}
                    handler={handleClose}
                    animate={{
                        mount: { scale: 1, y: 0 },
                        unmount: { scale: 0.9, y: -100 },
                    }}
                >
                    <DialogHeader>{selectedTimeline.eventName}</DialogHeader>
                    <DialogBody>
                        <p><strong>Date</strong>: {selectedTimeline.date}</p>
                        {selectedTimeline.location && <p><strong>Location:</strong> {selectedTimeline.location}</p>}
                        <br />
                        <p dangerouslySetInnerHTML={{ __html: selectedTimeline.eventDescription }} />
                        {selectedTimeline.googleMapsUrl && (
                            <div className="mt-4">
                                <iframe
                                    src={selectedTimeline.googleMapsUrl}
                                    width="100%"
                                    height="300"
                                    style={{ border: 0 }}
                                    allowFullScreen
                                    loading="lazy"
                                    title="Location Map"
                                ></iframe>
                            </div>
                        )}
                    </DialogBody>
                    <DialogFooter>
                        <Button variant="text" color="red" onClick={handleClose} className="mr-1">
                            <span>Close</span>
                        </Button>
                    </DialogFooter>
                </Dialog>
            )}
        </section>
    );
};

// Timeline component unchanged
const Timeline = ({ timeline, onButtonClick }) => {
    return (
        <div
            className="relative cursor-pointer rounded-lg border bg-white p-6 shadow-2xl transition-all duration-300 
               hover:scale-[1.03] hover:shadow-3xl hover:border-red-500 
               flex flex-col border-gray-200"
            onClick={() => onButtonClick(timeline)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onButtonClick(timeline); }}
            aria-label={`View details for event ${timeline.eventName}`}
        >
            <h2 className="text-3xl font-extrabold leading-tight text-gray-900 md:text-4xl lg:text-5xl mb-2">
                {timeline.eventName}
            </h2>
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide select-none mb-2">
                {timeline.date}
            </p>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onButtonClick(timeline);
                }}
                className="self-start rounded-md bg-gradient-to-r from-red-500 to-red-600 px-6 py-2 text-base font-semibold text-white shadow-md transition-colors duration-300 hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 mb-2"
                style={{ width: 'auto' }}
            >
                View Event
            </button>

            <div
                className="prose max-w-none text-gray-700 line-clamp-4"
                dangerouslySetInnerHTML={{ __html: timeline.eventDescription }}
            />
        </div>
    );
};